import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DotsHorizontalIcon,
  Pencil1Icon,
  ArrowUpIcon,
  EyeOpenIcon,
  ArrowLeftIcon,
  CodeIcon,
  BarChartIcon,
} from '@radix-ui/react-icons'
import { Sparkles } from 'lucide-react'
import useSWRMutation from 'swr/mutation'
import { putFetcher } from 'api/swr'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
  DropdownMenuSubContent,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { ContentLangs, LevelOptions } from 'config'
import { useToast } from '@/components/ui/use-toast'
import { useMutate } from './index'

export const DataTableRowActions = ({ row }) => {
  const navigate = useNavigate()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
        >
          <DotsHorizontalIcon className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[200px]">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <StickyTop row={row} />
        <SetLevel row={row} />
        <ShowLangs row={row} />
        <DropdownMenuItem
          onSelect={() =>
            navigate(`/admin/frames/${row.original.id}/report-summary`)
          }
        >
          <BarChartIcon className="w-4 h-4 mr-2" />
          Report
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => window.open(`/editor/${row.original.id}`, '_blank')}
        >
          <Pencil1Icon className="w-4 h-4 mr-2" />
          Editor
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a
            href={`/admin/ops-tools?opsToolsTab=template-tag-recommend&tid=${encodeURIComponent(row.original.id)}`}
            target="_blank"
            rel="noreferrer"
          >
            <Sparkles className="mr-2 h-4 w-4 shrink-0 fill-amber-300 text-fuchsia-500" />
            AI 打标签
          </a>
        </DropdownMenuItem>
        <DropdownMenuLabel className="text-red-600 font-semibold">
          Danger Zone
        </DropdownMenuLabel>
        <Offline row={row} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const StickyTop = ({ row }) => {
  const data = row.original
  const { trigger, isMutating } = useSWRMutation(
    `/frames/${data.id}`,
    putFetcher,
  )

  const handleClick = (e) => {
    e.preventDefault()
    trigger({ key: 'stickyTop' })
  }

  return (
    <>
      <DropdownMenuItem onSelect={handleClick}>
        <ArrowUpIcon className="mr-2 h-4 w-4" />
        <span>Sticky Top{isMutating ? '...' : null}</span>
      </DropdownMenuItem>
    </>
  )
}

const SetLevel = ({ row }) => {
  const data = row.original
  const { toast } = useToast()
  const mutate = useMutate()
  const { trigger, isMutating } = useSWRMutation(
    `/frames/${data.id}`,
    putFetcher,
  )
  const [open, setOpen] = React.useState(false)
  const [pendingLevel, setPendingLevel] = React.useState(null)

  const handleSelectLevel = (e, level) => {
    e.preventDefault()
    setPendingLevel(level)
    setOpen(true)
  }

  const handleConfirm = async (e) => {
    e.preventDefault()
    try {
      await trigger({ key: 'level', value: pendingLevel })
      toast({
        title: 'Success',
        description: `Set level to ${pendingLevel} successfully`,
        className: 'bg-green-500 text-white',
      })
      setOpen(false)
      if (mutate && typeof mutate === 'function') {
        mutate()
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Set level failed: ' + (error.message || 'Unknown Error'),
        variant: 'destructive',
      })
    }
  }

  return (
    <>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <CodeIcon className="w-4 h-4 mr-2" /> Set Level
          {isMutating && 'ing...'}
        </DropdownMenuSubTrigger>
        <DropdownMenuPortal>
          <DropdownMenuSubContent>
            {LevelOptions.map((level) => (
              <DropdownMenuCheckboxItem
                key={level.name}
                checked={Number(level.code) === Number(data.level)}
                onSelect={(e) => handleSelectLevel(e, level.code)}
              >
                {level.name}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuPortal>
      </DropdownMenuSub>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm to set Level</DialogTitle>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isMutating}>
              {isMutating ? 'Processing...' : 'Confirm'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

const ShowLangs = ({ row }) => {
  const data = row.original
  const curLangs = data?.contentLangs || []

  return (
    <>
      <Dialog>
        <DialogTrigger className="w-full">
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            <EyeOpenIcon className="mr-2 h-4 w-4" /> Langs
          </DropdownMenuItem>
        </DialogTrigger>
        <DialogContent className="max-h-screen overflow-y-auto">
          <DialogHeader className={'flex'}>
            <DialogTitle>Show Langs</DialogTitle>
          </DialogHeader>
          <div className="d-flex flex-wrap">
            {ContentLangs.slice(1).map((item) => (
              <Badge
                key={item.name}
                className={'me-1'}
                variant={!curLangs.includes(item.code) ? 'outline' : ''}
              >
                <small>
                  {item.name}-{item.code}
                </small>
              </Badge>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

const Offline = ({ row }) => {
  const data = row.original
  const { toast } = useToast()
  const mutate = useMutate()
  const { trigger, isMutating } = useSWRMutation(
    `/frames/${data.id}`,
    putFetcher,
  )
  const [open, setOpen] = React.useState(false)

  const handleOpen = (e) => {
    e.preventDefault()
    setOpen(true)
  }

  const handleConfirm = async (e) => {
    e.preventDefault()
    try {
      await trigger({ key: 'offline' })
      toast({
        title: 'Success',
        description: 'Offline Successfully',
        className: 'bg-green-500 text-white',
      })
      setOpen(false)
      if (mutate && typeof mutate === 'function') {
        mutate()
      } else {
        console.warn('mutate is not available or not a function')
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Offline Failed: ' + (error.message || 'Unknown Error'),
        variant: 'destructive',
      })
    }
  }

  return (
    <>
      <DropdownMenuItem className="text-red-500" onSelect={handleOpen}>
        <ArrowLeftIcon className="mr-2 h-4 w-4" />
        <span>offline{isMutating ? '...' : null}</span>
      </DropdownMenuItem>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Make sure to set offline</DialogTitle>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={isMutating}
            >
              {isMutating ? 'Processing...' : 'Confirm'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
